/**
 * useDualCtaVisibility
 *
 * Lee los flags de visibilidad + estado disabled + label overrides de los 2
 * botones (VIP + Free) per surface (intl `/` y funnel `/membership` o AR `/ser-miembro`) desde el
 * endpoint público /api/dual-cta-visibility.
 *
 * Decisión: NO hacemos polling con interval (a diferencia de useLiveCTA). Esto
 * es config "set and forget" del admin que cambia con baja frecuencia. Pero
 * SÍ revalidamos en:
 *   1. mount (fetch inicial)
 *   2. `visibilitychange` → "visible" (admin volvió al tab tras editar en otro)
 *   3. `storage` event con key `DUAL_CTA_VISIBILITY_UPDATED_LS_KEY` (cross-tab
 *      signal del admin tras save exitoso)
 *   4. custom event `DUAL_CTA_VISIBILITY_UPDATED_EVENT` (same-tab signal)
 *
 * Esto garantiza que el cambio del admin "sea real" en sus tabs abiertos sin
 * esperar al próximo pageview/SPA-nav.
 *
 * Estados per botón:
 *   - vipEnabled / freeEnabled  → false oculta totalmente
 *   - vipDisabled / freeDisabled → true muestra GRIS no clickeable (coming soon)
 *   - vipLabel*  / freeLabel*    → null usa cascada estándar; non-null pisa
 *
 * Defaults: TODOS visible + activo (no disabled) + sin label overrides.
 */

import { useEffect, useState, useCallback } from "react";

/** localStorage key — firma cross-tab del admin tras save exitoso. */
export const DUAL_CTA_VISIBILITY_UPDATED_LS_KEY = "dual_cta_visibility_updated";
/** CustomEvent name — firma same-tab del admin tras save exitoso. */
export const DUAL_CTA_VISIBILITY_UPDATED_EVENT = "dual-cta-visibility-updated";

export interface SurfaceButtonState {
  vipEnabled: boolean;
  freeEnabled: boolean;
  /** Botón gris no clickeable — visible pero deshabilitado. */
  vipDisabled: boolean;
  freeDisabled: boolean;
  /** Label overrides per idioma. null = cascada estándar. */
  vipLabelEs: string | null;
  vipLabelEn: string | null;
  freeLabelEs: string | null;
  freeLabelEn: string | null;
}

export interface DualCtaVisibility {
  intl: SurfaceButtonState;
  serMiembro: SurfaceButtonState;
}

const DEFAULT_SURFACE: SurfaceButtonState = {
  vipEnabled: true,
  /** Free OFF por defecto (solo VIP). Admin enciende en /admin/dual-cta-visibility. */
  freeEnabled: false,
  vipDisabled: false,
  freeDisabled: false,
  vipLabelEs: null,
  vipLabelEn: null,
  freeLabelEs: null,
  freeLabelEn: null,
};

const DEFAULTS_VIP_ONLY: DualCtaVisibility = {
  intl: { ...DEFAULT_SURFACE },
  serMiembro: { ...DEFAULT_SURFACE },
};

function normalizeSurface(raw: Partial<SurfaceButtonState> | undefined): SurfaceButtonState {
  if (!raw) return { ...DEFAULT_SURFACE };
  return {
    vipEnabled: raw.vipEnabled !== false,
    // Free es opt-in: solo true si el API lo manda explícitamente true.
    freeEnabled: raw.freeEnabled === true,
    vipDisabled: raw.vipDisabled === true,
    freeDisabled: raw.freeDisabled === true,
    vipLabelEs: typeof raw.vipLabelEs === "string" && raw.vipLabelEs.length > 0 ? raw.vipLabelEs : null,
    vipLabelEn: typeof raw.vipLabelEn === "string" && raw.vipLabelEn.length > 0 ? raw.vipLabelEn : null,
    freeLabelEs: typeof raw.freeLabelEs === "string" && raw.freeLabelEs.length > 0 ? raw.freeLabelEs : null,
    freeLabelEn: typeof raw.freeLabelEn === "string" && raw.freeLabelEn.length > 0 ? raw.freeLabelEn : null,
  };
}

export function useDualCtaVisibility(): DualCtaVisibility {
  const [state, setState] = useState<DualCtaVisibility>(DEFAULTS_VIP_ONLY);

  const revalidate = useCallback(async () => {
    try {
      const res = await fetch("/api/dual-cta-visibility", {
        headers: { "Cache-Control": "no-cache" },
      });
      if (!res.ok) return;
      const data = (await res.json()) as DualCtaVisibility | null;
      if (!data) return;
      setState({
        intl: normalizeSurface(data.intl),
        serMiembro: normalizeSurface(data.serMiembro),
      });
    } catch {
      // best-effort — defaults / estado previo se preservan
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await revalidate();
      if (cancelled) return;
    })();

    if (typeof window === "undefined" || typeof document === "undefined") {
      return () => {
        cancelled = true;
      };
    }

    // Cross-tab signal: admin guardó en otra tab → revalidamos acá sin esperar
    // al próximo pageview. Patrón consistente con useTemplateSelection
    // (WISHLIST_CONFIG_UPDATED_*).
    const onStorage = (e: StorageEvent) => {
      if (e.key === DUAL_CTA_VISIBILITY_UPDATED_LS_KEY) {
        void revalidate();
      }
    };
    const onLocalUpdate = () => {
      void revalidate();
    };
    // Tab volvió a foreground: el setInterval del browser puede haber sido
    // throttled (o no había timer), y el admin pudo haber tocado el flag
    // mientras tanto. Revalidamos al pasar a "visible".
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void revalidate();
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(
      DUAL_CTA_VISIBILITY_UPDATED_EVENT,
      onLocalUpdate as EventListener,
    );
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(
        DUAL_CTA_VISIBILITY_UPDATED_EVENT,
        onLocalUpdate as EventListener,
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [revalidate]);

  return state;
}
